<template>
  <div
    v-click-outside="vsoConfig"
    class="f-dropdown-overlay"
    @click.self.stop="hideDropdown"
  >
    <div
      v-if="bubbleShown"
      class="speech-bubble text-gray translate"
      :style="{
        top: bubbleTop + 'px',
        left: bubbleLeft + 5 + 'px',
      }"
    >
      Effacer
    </div>
    <div
      ref="wrapper"
      class="wrapper"
      :style="dropdownDisplay"
    >
      <slot name="main"></slot>
      <div
        v-if="showNoOptions && (!list || Object.keys(list).length === 0)"
        class="f-dropdown f-element translate not-allowed"
      >
        - Pas de données -
      </div>
      <div
        v-if="list || type === 'datepicker'"
        class="f-dropdown"
        :style="bodyDisplay"
        :class="{datepicker: type === 'datepicker', wide, reverse}"
      >
        <FygrInput
          v-if="needSearch"
          class="search-inp"
          icon="search"
          placeholder=""
          no-prompt
          exclude-translation-placeholder
          autofocus
          @click.stop=""
          @input="filterEvent($event)"
        ></FygrInput>
        <div
          v-if="list"
          class="f-elements-holder"
        >
          <div
            v-for="(el, i) in list.length
              ? listFiltered || list
              : Object.entries(listFiltered || list)"
            :key="`el-${el[0]}-${i}`"
            :style="`text-align :  ${textAlign}`"
            :title="complex && el[1] && el[1].disabled ? el[1].title : null"
            :class="{
              small,
              clickable: !(complex && el[1] && el[1].disabled),
              'f-element': true,
              'disabled-option': complex && el[1] && el[1].disabled,
              selected:
                highlightSelected && (el[0] === selected || el[1] === selected),
              translate: needTranslation,
            }"
            @click="
              !(complex && el[1] && el[1].disabled) && setValue(el[0])
            "
          >
            <div class="name">
              {{ complex ? el[1].name : el[1] }}
              <div
                v-if="removable && el[1].removable"
                class="remove-wrap"
                @mouseenter="showBubble($event, true)"
                @mouseleave="showBubble($event, false)"
                @mousedown.stop.prevent="
                  $emit('remove', el[0]);
                  showBubble($event, false);
                "
              >
                <div class="remove" />
              </div>
            </div>
          </div>
        </div>
        <DatePicker
          v-else-if="type === 'datepicker'"
          v-model="preSelected"
          :locale="getLocale"
          :disabled-dates="disabledDates"
          color="purple"
          is-inline
          @dayclick="
            (itm, event) => {
              if (itm?.isDisabled) return;
              event.target.blur();
              setValue(itm);
            }
          "
        />
      </div>
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
import {DatePicker} from 'v-calendar';
import {mapGetters, mapState} from 'vuex';
import vClickOutside from 'click-outside-vue3';
import {datesMixin} from '@/mixins/datesMixin';

export default {
  name: 'Dropdown',
  directives: {
    clickOutside: vClickOutside.directive,
  },
  components: {
    DatePicker,
  },
  mixins: [datesMixin],
  props: {
    list: {
      type: Object,
      default: () => {},
    },
    dropdownStyle: {
      type: Object,
      default: () => {},
    },
    selected: {
      type: [String, Date, Object, Boolean],
      default: () => '',
    },
    disabledDates: {
      type: [String, Date, Object],
      default: () => '',
    },
    type: {
      type: String,
      default: () => '',
    },
    textAlign: {
      type: String,
      default: () => 'left',
    },
    needSearch: {
      type: Boolean,
      default: false,
    },
    small: Boolean,
    wide: {
      type: Number,
      default: null,
    },
    needTranslation: {
      type: Boolean,
      default: false,
    },
    showNoOptions: {
      type: Boolean,
      default: false,
    },
    endOfDay: {
      type: Boolean,
      default: false,
    },
    eventContainer: {
      type: Object,
      default: () => {},
    },
    highlightSelected: {
      type: Boolean,
      default: false,
    },
    complex: Boolean,
    removable: Boolean,
    align: {
      type: String,
      default: 'right',
    },
  },
  emits: ['selected', 'hide', 'remove'],
  data() {
    return {
      vsoConfig: {
        handler: () => this.$emit('hide'),
        events: ['mousedown'],
      },
      styleSet: {},
      listFiltered: null,
      preSelected: this.getDate().format('YYYY-MM-DD'),
      bubbleLeft: 0,
      bubbleShown: false,
      bubbleTop: 0,
      // When source.width is 'auto' the wrapper sizes to its content. We
      // need that real width for the right/left alignment math, so we
      // measure it on next tick (and again whenever the trigger changes).
      measuredWidth: 0,
    };
  },

  computed: {
    ...mapState('app', ['scaleRatio']),
    ...mapGetters('user', ['getLocale']),
    headerStyle() {
      return {
        ...this.dropdownStyle,
        top: `${Number(this.dropdownStyle.top.slice(0, -2)) - 41 / this.scaleRatio}px`,
        'min-width': `${Number(this.dropdownStyle['min-width'].slice(0, -2)) - 10 / this.scaleRatio}px`,
      };
    },
    reverse() {
      const source = this.styleSet || this.dropdownStyle;
      let height = source['max-height'] || source.height;
      height = height === 'auto' || height === '100%' ? 0 : height;
      return (
        source.bottom && !(source.bottom - height - 28 / this.scaleRatio > 0)
      );
    },
    bodyDisplay() {
      const source = this.styleSet || this.dropdownStyle;
      return {
        'max-height': `${source['max-height']}px`,
        height: `${source.height}px`,
      };
    },
    //
    // IMPORTANT! to achieve correct positioning of dropdown (especially when we use it like wrapper) we need to adjust 'max-height' property in
    // component that uses this dropdown, otherwise calculation will be provided based on default 260px height. Example - if DD is used
    // as wrapper with <slot> and you want to have 300px of its height you need use  style: {...  'max-height': 300 ...}
    //
    dropdownDisplay() {
      const source = this.styleSet || this.dropdownStyle;
      const isAutoWidth = source.width === 'auto';
      const dict = {
        top: `${source.top + 5}px`,
        'min-width': `${source['min-dd-width'] || source['min-width']}px`,
        'max-width': `${source['max-width']}px`,
      };
      if (!isAutoWidth) {
        dict.width = `${source.width}px`;
      }
      const widthForCalc = isAutoWidth
        ? this.measuredWidth || 0
        : parseFloat(source.width) || 0;
      const minWidth =
        parseFloat(
          source['min-dd-width'] || source['min-width'] || widthForCalc,
        ) || 0;
      const maxWidth = parseFloat(source['max-width']) || Infinity;
      const effectiveWidth = Math.min(
        Math.max(minWidth, widthForCalc),
        maxWidth,
      );
      const triggerRight = window.innerWidth - (parseFloat(source.right) || 0);
      if (this.align === 'left' || triggerRight < effectiveWidth) {
        dict.left = `${Math.max(0, source.left || 0)}px`;
      } else {
        dict.right = `${source.right}px`;
      }
      if (this.reverse) {
        dict.top = 'unset';
        const height = source['max-height'] || source.height;
        const shift =
          source.innerHeight -
          75 / this.scaleRatio -
          source.bottom -
          height -
          12 / this.scaleRatio -
          source.targetHeight -
          5;
        if (shift < 0) {
          dict.bottom = `${source.bottom + source.targetHeight + shift}px`;
        } else {
          dict.bottom = `${source.bottom + 5 + source.targetHeight}px`;
        }
      }
      return dict;
    },
  },
  watch: {
    selected: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.preSelected = this.getDate(newValue)
            .hour(12)
            .minute(0)
            .second(0)
            .millisecond(0)
            .format('YYYY-MM-DD HH:mm:ss');
        }
      },
    },
    eventContainer: {
      deep: true,
      handler(newValue) {
        if (newValue && Object.keys(newValue).length) {
          // Wait one frame so layout has settled (matches mounted()).
          requestAnimationFrame(() => this.setStyle(newValue));
          this.scheduleMeasure();
        }
      },
    },
  },
  created() {
    if (this.eventContainer && Object.keys(this.eventContainer).length) {
      this.setStyle(this.eventContainer);
    }
  },
  mounted() {
    if (this.eventContainer && Object.keys(this.eventContainer).length) {
      requestAnimationFrame(() => this.setStyle(this.eventContainer));
    }
    this.scheduleMeasure();
  },
  beforeUnmount() {
    this.preSelected = this.getDate().format('YYYY-MM-DD');
  },
  methods: {
    scheduleMeasure() {
      // The wrapper may not be in the DOM yet on the first tick, so wait
      // for paint before reading offsetWidth. Done as RAF after nextTick
      // because v-calendar can adjust its own width after first render.
      this.$nextTick(() => {
        const update = () => {
          const el = this.$refs.wrapper;
          if (el && el.offsetWidth) this.measuredWidth = el.offsetWidth;
        };
        update();
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(update);
        }
      });
    },
    setStyle({e, container, style}) {
      if (!container) {
        return;
      }
      const trigger =
        (e?.target?.closest && e.target.closest(container)) ||
        e?.currentTarget ||
        e?.target ||
        null;
      const rect = trigger?.getBoundingClientRect?.() ?? {
        top: e?.clientY ?? 0,
        bottom: e?.clientY ?? 0,
        left: e?.clientX ?? 0,
        right: e?.clientX ?? 0,
        width: 0,
        height: 0,
      };
      const viewWidth = e?.view?.innerWidth ?? window.innerWidth;
      const viewHeight = e?.view?.innerHeight ?? window.innerHeight;
      const ratio = this.scaleRatio || 1;
      // Diagnostics — see CategoriesDropdownRectangle for rationale.
      if (!trigger) {
        console.warn('[DD][Dropdown] setStyle: trigger not found', {
          container,
          e,
        });
      }
      if (trigger && !document.body.contains(trigger)) {
        console.warn('[DD][Dropdown] setStyle: trigger detached from DOM', {
          trigger,
        });
      }
      if (rect.width === 0 && rect.height === 0) {
        console.warn('[DD][Dropdown] setStyle: zero rect', {rect, trigger});
      }
      if (!Number.isFinite(this.scaleRatio) || this.scaleRatio <= 0) {
        console.warn('[DD][Dropdown] setStyle: bad scaleRatio', {
          scaleRatio: this.scaleRatio,
        });
      }
      this.styleSet = {
        top: rect.bottom,
        right: viewWidth - rect.right,
        left: rect.left,
        width: this.type === 'datepicker' ? 'auto' : rect.width - 10,
        height: 260 / ratio,
        bottom: viewHeight - rect.bottom,
        hh: 35 / ratio,
        targetHeight: rect.height,
        innerHeight: viewHeight,
        ...style,
      };
      const overlayRect = this.$el?.getBoundingClientRect?.();
      if (overlayRect && (overlayRect.top !== 0 || overlayRect.left !== 0)) {
        this.styleSet.top = rect.bottom - overlayRect.top;
        this.styleSet.right = overlayRect.right - rect.right;
      }
    },
    filterEvent(search) {
      const term = (search || '').toLowerCase();
      if (Array.isArray(this.list)) {
        this.listFiltered = this.list.filter((el) => {
          const label = this.complex ? el[1]?.name ?? '' : el[1] ?? '';
          return (
            String(label).toLowerCase().includes(term) ||
            String(el[0]).toLowerCase().includes(term)
          );
        });
        return;
      }
      this.listFiltered = Object.keys(this.list)
        .filter((code) => code.toLowerCase().includes(term))
        .reduce((obj, key) => {
          obj[key] = this.list[key];
          return obj;
        }, {});
    },
    showBubble(e, show) {
      this.bubbleTop = (e.clientY - e.offsetY) / this.scaleRatio - 40;
      this.bubbleLeft = (e.clientX - e.offsetX) / this.scaleRatio - 30;
      this.bubbleShown = show;
    },
    setValue(val) {
      if (this.type === 'datepicker') {
        let date = '';
        if (this.endOfDay) {
          date = this.getDate(val.date).endOf('day');
        } else {
          // we set it at 3am because of utc time and conversion in CET and CEST
          date = this.getDate(val.date).set('hour', 3);
        }
        this.$emit('selected', date);
      } else {
        this.$emit('selected', val);
      }
    },
    hideDropdown(e) {
      if (
        e.target.closest('.vc-header') ||
        e.target.closest('.vc-arrows-container')
      ) {
        return;
      }
      this.$emit('hide');
    },
  },
};
</script>

<style lang="scss" scoped>
.f-dropdown-overlay {
  position: fixed;
  z-index: 190;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  border-radius: var(--px4);
  .wrapper {
    position: absolute;
    background: var(--fygr-surface-raised);
    padding: var(--px5);
    border-radius: var(--px4);
    display: flex;
    flex-direction: column;
    border: 1px solid var(--fygr-line);
    transform-origin: top;
    animation: expandY 0.25s;
    box-shadow: 0 1px 4px rgba(46, 7, 41, 0.08);
    .f-dropdown {
      z-index: 100;
      display: flex;
      flex-direction: column;
      padding: var(--px10);
      padding-right: 0;
      top: var(--px211);
      right: var(--px38);
      background-color: var(--fygr-surface-raised);
      border-radius: 0 0 var(--px4) var(--px4);
      box-shadow: none;
      min-width: var(--px50);
      height: calc(100% - var(--px20));
      &.reverse {
        flex-direction: column-reverse;
        .search-inp {
          margin-bottom: 0;
          margin-top: var(--px20);
        }
      }
      &.wide {
        border-radius: 0 var(--px4) var(--px4) var(--px4);
      }
      &.datepicker {
        width: var(--px260);
        border-radius: 0 var(--px4) var(--px4) var(--px4);
      }
      .f-elements-holder {
        padding-right: var(--px8);
        overflow: auto;
        .f-element {
          position: relative;
          min-height: var(--px32);
          display: flex;
          justify-content: center;
          border-bottom: 1px solid var(--fygr-line);
          padding: var(--px6) var(--px5);
          flex-direction: column;
          border-radius: var(--px2);
          box-sizing: border-box;
          &.small {
            min-height: var(--px26);
          }
          &.selected {
            background-color: $light-blue;
          }
          &.disabled-option {
            opacity: 0.5;
            cursor: not-allowed;
          }
          &:hover:not(.disabled-option) {
            background-color: var(--aubergine);
            color: white;
          }
        }
      }
    }
  }
}
:deep(.vc-title),
:deep(.vc-w-12) {
  text-transform: uppercase;
  font-size: var(--px14);
}
:deep(.vc-border) {
  border: none;
}
:deep(.vc-rounded-full) {
  border-radius: 0;
}
:deep(.vc-font-medium) {
  font-weight: initial;
}
:deep(.vc-text-gray-500) {
  color: var(--fygr-text-fade);
  font-weight: initial;
}
:deep(.vc-text-gray-600) {
  color: var(--fygr-text-fade);
  font-weight: initial;
  &:hover {
    background-color: var(--fygr-hover-overlay);
    color: var(--fygr-text-fade);
    opacity: 1;
  }
}
:deep(.vc-container) {
  --day-content-bg-color-hover: none;
  --day-content-bg-color-focus: none;
  --highlight-height: calc(100% - 3.2px);
  border: none;
}
:deep(.vc-bg-blue-600) {
  background-color: var(--aubergine);
}
:deep(.vc-highlight) {
  margin: 1.6px 0;
}
:deep(.vc-bg-gray-800) {
  background-color: var(--fygr-ebony);
}
:deep(.vc-day-content[class]) {
  &:focus:not(.vc-font-bold) {
    color: var(--text-primary);
    font-weight: initial;
  }
}
:deep(.vc-day-content) {
  &:hover:not(.vc-font-bold) {
    background-color: var(--fygr-hover-overlay);
    color: var(--text-primary);
  }
}
.search-inp {
  width: calc(100% - var(--px2));
  margin: var(--px-5) var(--px-5) var(--px10) var(--px-5);
  max-height: var(--px35);
  :deep(.icon) {
    left: var(--px10);
  }
  :deep(input.with-icon) {
    padding-left: var(--px28);
  }
}
.speech-bubble {
  left: 50%;
  z-index: 111;
}
.speech-bubble:after {
  left: 50%;
}
.name {
  display: flex;
  align-items: center;
  font-size: var(--px14);
}
.remove-wrap {
  display: flex;
  align-items: center;
  margin-left: auto;
  min-height: var(--px20);
}
.remove {
  min-height: var(--px2);
  max-height: var(--px2);
  min-width: var(--px15);
  border-radius: var(--px1);
  background-color: var(--fygr-text-fade);
}
.not-allowed {
  cursor: not-allowed;
}
</style>
